<script setup lang="ts">
import { computed, type PropType, ref, watch } from "vue";
import { TransitionEasing } from "@/models/types";

defineOptions({ name: "BcCollapseTransition" });

const props = defineProps({
  name: {
    type: String,
    required: false,
    default: "collapse",
  },
  dimension: {
    type: String,
    required: false,
    default: "height",
    validator: (value: any) =>
      typeof value === "string" && ["height", "width"].includes(value),
  },
  duration: {
    type: Number,
    required: false,
    default: 300,
  },
  easing: {
    type: String as PropType<TransitionEasing>,
    required: false,
    default: "ease-in-out",
    validator: (value: any) => TransitionEasing.isTransitionEasing(value),
  },
});

const emit = defineEmits<{
  (e: "after-appear", el: HTMLElement): void;
  (e: "after-enter", el: HTMLElement): void;
  (e: "after-leave", el: HTMLElement): void;
  (e: "appear", el: HTMLElement): void;
  (e: "appear-cancelled", el: HTMLElement): void;
  (e: "before-appear", el: HTMLElement): void;
  (e: "before-enter", el: HTMLElement): void;
  (e: "before-leave", el: HTMLElement): void;
  (e: "enter", el: HTMLElement, done: TimerHandler): void;
  (e: "enter-cancelled", el: HTMLElement): void;
  (e: "leave", el: HTMLElement, done: TimerHandler): void;
  (e: "leave-cancelled", el: HTMLElement): void;
  (e: "toggle", burgerMenuOpened: boolean): void;
}>();

const cachedStyles = ref<any>(null);

const transition = computed(() => {
  let transitions = new Array<string>();
  if (cachedStyles.value) {
    Object.keys(cachedStyles.value).forEach((key) => {
      transitions.push(
        `${convertToCssProperty(key)} ${props.duration}ms ${props.easing}`
      );
    });
  }
  return transitions.join(", ");
});

function beforeAppear(el: Element): void {
  // Emit the event to the parent
  emit("before-appear", el as HTMLElement);
}

function appear(el: Element): void {
  // Emit the event to the parent
  emit("appear", el as HTMLElement);
}

function afterAppear(el: Element): void {
  // Emit the event to the parent
  emit("after-appear", el as HTMLElement);
}

function appearCancelled(el: Element): void {
  // Emit the event to the parent
  emit("appear-cancelled", el as HTMLElement);
}

function beforeEnter(el: Element): void {
  // Emit the event to the parent
  emit("before-enter", el as HTMLElement);
}

function enter(el: Element, done: TimerHandler): void {
  // Because width and height may be 'auto',
  // first detect and cache the dimensions
  detectAndCacheDimensions(el as HTMLElement);

  // The order of applying styles is important:
  // - 1. Set styles for state before transition
  // - 2. Force repaint
  // - 3. Add transition style
  // - 4. Set styles for state after transition
  // If the order is not right and you open any 2nd level submenu
  // for the first time, the transition will not work.
  setClosedDimensions(el as HTMLElement);
  hideOverflow(el as HTMLElement);
  forceRepaint(el as HTMLElement);
  setTransition(el as HTMLElement);
  setOpenedDimensions(el as HTMLElement);

  // Emit the event to the parent
  emit("enter", el as HTMLElement, done);

  // Call done() when the transition ends
  // to trigger the @after-enter event.
  setTimeout(done, props.duration);
}

function afterEnter(el: Element): void {
  // Clean up inline styles
  unsetOverflow(el as HTMLElement);
  unsetTransition(el as HTMLElement);
  unsetDimensions(el as HTMLElement);
  clearCachedDimensions();

  // Emit the event to the parent
  emit("after-enter", el as HTMLElement);
}

function enterCancelled(el: Element): void {
  // Emit the event to the parent
  emit("enter-cancelled", el as HTMLElement);
}

function beforeLeave(el: Element): void {
  // Emit the event to the parent
  emit("before-leave", el as HTMLElement);
}

function leave(el: Element, done: TimerHandler): void {
  // For some reason, @leave triggered when starting
  // from open state on page load. So for safety,
  // check if the dimensions have been cached.
  detectAndCacheDimensions(el as HTMLElement);

  // The order of applying styles is less important
  // than in the enter phase, as long as we repaint
  // before setting the closed dimensions.
  // But it is probably best to use the same
  // order as the enter phase.
  setOpenedDimensions(el as HTMLElement);
  hideOverflow(el as HTMLElement);
  forceRepaint(el as HTMLElement);
  setTransition(el as HTMLElement);
  setClosedDimensions(el as HTMLElement);

  // Emit the event to the parent
  emit("leave", el as HTMLElement, done);

  // Call done() when the transition ends
  // to trigger the @after-leave event.
  // This will also cause v-show
  // to reapply 'display: none'.
  setTimeout(done, props.duration);
}

function afterLeave(el: Element): void {
  // Clean up inline styles
  unsetOverflow(el as HTMLElement);
  unsetTransition(el as HTMLElement);
  unsetDimensions(el as HTMLElement);
  clearCachedDimensions();

  // Emit the event to the parent
  emit("after-leave", el as HTMLElement);
}

function leaveCancelled(el: Element): void {
  // Emit the event to the parent
  emit("leave-cancelled", el as HTMLElement);
}

function detectAndCacheDimensions(el: HTMLElement): void {
  // Cache actual dimensions
  // only once to void invalid values when
  // triggering during a transition
  if (cachedStyles.value) return;

  const visibility = el.style.visibility;
  const display = el.style.display;

  // Trick to get the width and
  // height of a hidden element
  el.style.visibility = "hidden";
  el.style.display = "";

  cachedStyles.value = detectRelevantDimensions(el);

  // Restore any original styling
  el.style.visibility = visibility;
  el.style.display = display;
}
function clearCachedDimensions(): void {
  cachedStyles.value = null;
}

function detectRelevantDimensions(el: HTMLElement): any {
  // These properties will be transitioned
  if (props.dimension === "height") {
    return {
      borderTop: el.style.borderTop || getCssValue(el, "border-top"),
      marginTop: el.style.marginTop || getCssValue(el, "margin-top"),
      paddingTop: el.style.paddingTop || getCssValue(el, "padding-top"),
      height: el.style.height || getCssValue(el, "height"),
      paddingBottom: el.style.paddingBottom || getCssValue(el, "padding-bottom"),
      marginBottom: el.style.marginBottom || getCssValue(el, "margin-bottom"),
      borderBottom: el.style.borderBottom || getCssValue(el, "border-bottom"),
    };
  }

  if (props.dimension === "width") {
    return {
      borderLeft: el.style.borderLeft || getCssValue(el, "border-left"),
      marginLeft: el.style.marginLeft || getCssValue(el, "margin-left"),
      paddingLeft: el.style.paddingLeft || getCssValue(el, "padding-left"),
      width: el.style.width || getCssValue(el, "width"),
      paddingRight: el.style.paddingRight || getCssValue(el, "padding-right"),
      marginRight: el.style.marginRight || getCssValue(el, "margin-right"),
      borderRight: el.style.borderRight || getCssValue(el, "border-right"),
    };
  }

  return {};
}

function setTransition(el: HTMLElement): void {
  el.style.transition = transition.value;
}

function unsetTransition(el: HTMLElement): void {
  el.style.transition = "";
}

function hideOverflow(el: HTMLElement): void {
  el.style.overflow = "hidden";
}

function unsetOverflow(el: HTMLElement): void {
  el.style.overflow = "";
}

function setClosedDimensions(el: HTMLElement): void {
  Object.keys(cachedStyles.value)
    .slice()
    .reverse()
    .forEach((key) => {
      el.style.setProperty(convertToCssProperty(key), "0");
    });
}

function setOpenedDimensions(el: HTMLElement): void {
  Object.keys(cachedStyles.value).forEach((key) => {
    el.style.setProperty(convertToCssProperty(key), cachedStyles.value[key]);
  });
}

function unsetDimensions(el: HTMLElement): void {
  Object.keys(cachedStyles.value).forEach((key) => {
    el.style.setProperty(convertToCssProperty(key), "");
  });
}

function forceRepaint(el: HTMLElement): void {
  // Force repaint to make sure the animation is triggered correctly.
  // Thanks: https://markus.oberlehner.net/blog/transition-to-height-auto-with-vue/
  getComputedStyle(el).getPropertyValue(props.dimension);
}

function getCssValue(el: HTMLElement, style: string): string {
  return getComputedStyle(el, null).getPropertyValue(style);
}

function convertToCssProperty(style: string): string {
  // Example: convert 'paddingTop' to 'padding-top'
  // Thanks: https://gist.github.com/tan-yuki/3450323
  const upperChars = style.match(/([A-Z])/g);

  if (!upperChars) {
    return style;
  }

  for (let i = 0, n = upperChars.length; i < n; i++) {
    style = style.replace(new RegExp(upperChars[i]), "-" + upperChars[i].toLowerCase());
  }

  if (style.slice(0, 1) === "-") {
    style = style.slice(1);
  }

  return style;
}

watch(() => props.dimension, clearCachedDimensions);

defineExpose({
  transition,
});
</script>

<template>
  <transition
    :name="name"
    @before-appear="beforeAppear"
    @appear="appear"
    @after-appear="afterAppear"
    @appear-cancelled="appearCancelled"
    @before-enter="beforeEnter"
    @enter="enter"
    @after-enter="afterEnter"
    @enter-cancelled="enterCancelled"
    @before-leave="beforeLeave"
    @leave="leave"
    @after-leave="afterLeave"
    @leave-cancelled="leaveCancelled"
  >
    <slot />
  </transition>
</template>
